import React, { useState, useEffect } from 'react';
import { getCampaignCancelPreview, cancelCampaign } from '../api/api';
import './CancelCampaignModal.css';

const CancelCampaignModal = ({ isOpen, onClose, campaignId, campaignName, onCancelled }) => {
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (isOpen && campaignId) {
      fetchPreview();
    }
  }, [isOpen, campaignId]);

  const fetchPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCampaignCancelPreview(campaignId);
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load cancellation preview');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirmed) {
      setError('Please confirm that you want to cancel this campaign');
      return;
    }

    setCancelling(true);
    setError('');
    try {
      const response = await cancelCampaign(campaignId, reason);
      if (response.data.success) {
        onCancelled?.(response.data);
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel campaign');
    } finally {
      setCancelling(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '🟡';
      case 'declined': return '❌';
      case 'accepted': return '✅';
      case 'submitted': return '📝';
      default: return '⚪';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cancel-modal-overlay" onClick={onClose}>
      <div className="cancel-modal" onClick={e => e.stopPropagation()}>
        <div className="cancel-modal-header">
          <div className="header-icon">🗑️</div>
          <h2>Cancel Campaign</h2>
          <p className="campaign-name">{campaignName}</p>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="cancel-modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading cancellation preview...</p>
            </div>
          ) : error && !preview ? (
            <div className="error-state">
              <span className="error-icon">⚠️</span>
              <p>{error}</p>
              <button onClick={fetchPreview} className="retry-btn">Try Again</button>
            </div>
          ) : !preview?.can_cancel ? (
            <div className="cannot-cancel-state">
              <span className="warning-icon">🚫</span>
              <h3>Cannot Cancel Campaign</h3>
              <p>{preview?.reason}</p>
              <button onClick={onClose} className="btn-secondary">Close</button>
            </div>
          ) : (
            <>
              <div className="warning-banner">
                <span>⚠️</span>
                <p>This action cannot be undone. Review the breakdown carefully before confirming.</p>
              </div>

              {/* Creator Breakdown */}
              <div className="section creators-section">
                <h3>📊 Creator Breakdown</h3>
                <div className="creators-table-container">
                  <table className="creators-table">
                    <thead>
                      <tr>
                        <th>Creator</th>
                        <th>Status</th>
                        <th>Kill Fee</th>
                        <th>Your Refund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.creators_breakdown?.map((creator, idx) => (
                        <tr key={idx}>
                          <td className="creator-cell">
                            <span className="creator-name">{creator.creator_name}</span>
                            {creator.creator_username && (
                              <span className="creator-username">@{creator.creator_username}</span>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${creator.status}`}>
                              {getStatusIcon(creator.status)} {creator.status_label || creator.status}
                            </span>
                          </td>
                          <td className="amount-cell">
                            {creator.creator_payment > 0 ? (
                              <div className="payment-details">
                                <span className="payment-amount">{formatCurrency(creator.creator_payment)}</span>
                              </div>
                            ) : (
                              <span className="no-payment">₹0</span>
                            )}
                          </td>
                          <td className="amount-cell">
                            <span className="refund-amount">{formatCurrency(creator.brand_refund)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="section summary-section">
                <h3>💰 Financial Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Total Escrow Blocked</span>
                    <span className="summary-value">{formatCurrency(preview.summary?.escrow_amount)}</span>
                  </div>
                  <div className="summary-item muted">
                    <span className="summary-label">Platform Fee (3.5%)</span>
                    <span className="summary-value negative">-{formatCurrency(preview.summary?.platform_fee)}</span>
                    <span className="summary-note">Non-refundable</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Creator Allocation</span>
                    <span className="summary-value">{formatCurrency(preview.summary?.creator_allocation)}</span>
                  </div>
                  {preview.summary?.total_kill_fee_deduction > 0 && (
                    <div className="summary-item">
                      <span className="summary-label">Kill Fee (8.5%)</span>
                      <span className="summary-value negative">-{formatCurrency(preview.summary?.total_kill_fee_deduction)}</span>
                      <span className="summary-note">Deducted for creators who did work</span>
                    </div>
                  )}
                </div>
                <div className="summary-total-card">
                  <div className="summary-total-row">
                    <span className="summary-total-label">Your Refund</span>
                    <span className="summary-total-value">{formatCurrency(preview.summary?.net_refund)}</span>
                  </div>
                </div>
              </div>

              {/* Reason Input */}
              <div className="section reason-section">
                <label>Reason for cancellation (optional)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let us know why you're cancelling..."
                  rows={3}
                />
              </div>

              {/* Confirmation */}
              <div className="confirmation-section">
                <label className="confirm-checkbox">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>I understand this action cannot be undone and agree to the refund terms above</span>
                </label>
              </div>

              {error && <div className="error-message">{error}</div>}

              {/* Info Note */}
              <div className="info-note">
                <span>ℹ️</span>
                <p>
                  Creators will be notified and payments will be processed within 5-7 business days.
                  Your refund will be credited to your original payment method.
                </p>
              </div>
            </>
          )}
        </div>

        {preview?.can_cancel && (
          <div className="cancel-modal-footer">
            <button className="btn-secondary" onClick={onClose} disabled={cancelling}>
              Keep Campaign
            </button>
            <button
              className="btn-danger"
              onClick={handleCancel}
              disabled={cancelling || !confirmed}
            >
              {cancelling ? (
                <>
                  <span className="spinner-small"></span>
                  Cancelling...
                </>
              ) : (
                <>
                  🗑️ Confirm Cancellation
                  <span className="refund-preview">Refund: {formatCurrency(preview.summary?.net_refund)}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancelCampaignModal;
